import { ReportBlockTypes } from '@reports/domain/types';
import type { BlockBase } from './BlockBase';
import type { ColumnConfig, ColumnDefinition } from './ColumnConfig';

import { validateBlockBase } from './BlockBase';
import { validateColumnConfig, createColumnConfigMap } from './ColumnConfig';

/**
 * Value object representing a table block in a report
 *
 * Encapsulates table logic with validation and business rules.
 * Immutable by design to ensure data integrity.
 *
 * @property {columnToggleEnabled} - To show a dropdown to toggle the columns visibility
 * @property {columnsConfig} - ReadonlyMap of column definitions keyed by column key (key not duplicated in value)
 *
 * @example
 * const tableBlock: TableBlockConfig = {
 *   key: 'sales_table',
 *   title: 'Ventas de los productos',
 *   description: 'Ventas de los productos',
 *   endpoint: 'api/v1/sales/products',
 *   type: ReportBlockType.TABLE,
 *   columnsConfig: new Map([
 *     ['concept', { label: 'Concepto', format: { type: 'text' } }],
 *     ['value', { label: 'Valor', format: { type: 'currency' } }]
 *   ]),
 *   columnToggleEnabled: true
 * };
 */
export interface TableBlockConfig extends BlockBase {
  readonly type: typeof ReportBlockTypes.TABLE;
  readonly columnsConfig: ReadonlyMap<string, ColumnDefinition>;
  readonly columnToggleEnabled: boolean;
  readonly paginationEnabled?: boolean;
  readonly pageSize?: number;
}

type CreateTableBlockConfigProps = Omit<TableBlockConfig, 'columnsConfig'> & {
  columnsConfig: ColumnConfig[];
};

/**
 * Factory function to create a table block
 *
 * Accepts columnsConfig as an array and converts it to a ReadonlyMap internally.
 * The Map preserves insertion order for column rendering.
 *
 * @param props - The properties to create the table block (columnsConfig as array)
 * @returns {TableBlockConfig} The created table block with columnsConfig as ReadonlyMap
 *
 * @example
 * const tableBlock = createTableBlockConfig({
 *   key: 'sales_table',
 *   endpoint: 'api/v1/sales',
 *   type: ReportBlockTypes.TABLE,
 *   columnsConfig: [
 *     { key: 'concept', label: 'Concept', format: { type: 'text' } },
 *     { key: 'value', label: 'Value', format: { type: 'currency' } }
 *   ],
 *   columnToggleEnabled: false
 * });
 */
export const createTableBlockConfig = (props: CreateTableBlockConfigProps): TableBlockConfig => {
  validateBlockBase(props);
  validateColumnConfig(props.columnsConfig);

  const columnsConfigMap = createColumnConfigMap(props.columnsConfig);

  return Object.freeze({
    ...props,
    columnsConfig: columnsConfigMap,
  });
};
